import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { AppConfig } from "@src/config/AppConfig";
import { GetMasterDataProductPricesDTO } from "@src/domain/dto/masterData";
import type { MasterDataProductPrice } from "@src/domain/entities/masterData";
import { GetMasterDataProductPricesUseCase } from "@src/application/useCases/masterData";
import { MasterDataProductPriceRepository } from "@src/infrastructure/repositories/masterData";
import { useMasterDataListQuery } from "./masterData/useMasterDataListQuery";

interface MasterDataProductPricesDataContextValue {
  brandKey: string;
  productPrices: MasterDataProductPrice[];
  loading: boolean;
  error: string | null;
  search: string;
  setSearch: (search: string) => void;
  page: number;
  limit: number;
  total: number;
  setPage: (page: number) => void;
  refetch: () => void;
}

const MasterDataProductPricesDataContext =
  createContext<MasterDataProductPricesDataContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useMasterDataProductPrices(): MasterDataProductPricesDataContextValue {
  const context = useContext(MasterDataProductPricesDataContext);
  if (!context) {
    throw new Error(
      "useMasterDataProductPrices must be used within MasterDataProductPricesDataProvider",
    );
  }
  return context;
}

interface MasterDataProductPricesDataProviderProps {
  brandKey: string;
  children: ReactNode;
  defaultPageSize?: number;
}

export function MasterDataProductPricesDataProvider({
  brandKey,
  children,
  defaultPageSize = AppConfig.getDefaultPaginationLimit(),
}: Readonly<MasterDataProductPricesDataProviderProps>) {
  const getProductPricesUseCase = useMemo(
    () =>
      new GetMasterDataProductPricesUseCase(
        new MasterDataProductPriceRepository(),
      ),
    [],
  );

  const fetchPage = useCallback(
    async (args: { search: string | null; page: number; limit: number }) =>
      getProductPricesUseCase.execute(
        new GetMasterDataProductPricesDTO({
          includeInactive: true,
          search: args.search,
          page_number: args.page,
          page_size: args.limit,
        }),
      ),
    [getProductPricesUseCase],
  );

  const {
    items: productPrices,
    loading,
    error,
    search,
    setSearch,
    page,
    setPage,
    limit,
    total,
    refetch,
  } = useMasterDataListQuery({
    brandKey,
    defaultPageSize,
    errorMessage: "Failed to load master data product prices",
    cacheScope: "product-prices",
    fetchPage,
  });

  const value = useMemo(
    (): MasterDataProductPricesDataContextValue => ({
      brandKey,
      productPrices,
      loading,
      error,
      search,
      setSearch,
      page,
      limit,
      total,
      setPage,
      refetch,
    }),
    [
      brandKey,
      productPrices,
      loading,
      error,
      search,
      setSearch,
      page,
      limit,
      total,
      setPage,
      refetch,
    ],
  );

  return (
    <MasterDataProductPricesDataContext.Provider value={value}>
      {children}
    </MasterDataProductPricesDataContext.Provider>
  );
}
